import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { COMPANY } from "@/data/company";

export default function Terms() {
  return (
    <div>
      <PageHero
        eyebrow="Legal"
        title="Terms & Conditions"
        description="Effective date: 1 April 2026 · Please read these terms carefully before using our website or services."
      />
      <section className="section bg-white">
        <article className="container-nago prose prose-slate max-w-3xl">
          <p>
            These Terms &amp; Conditions govern your access to and use of the website{" "}
            <strong>{COMPANY.website}</strong> and any products, services, quotations or
            information provided by Nago International. By using this website or engaging with
            us, you agree to these terms.
          </p>

          <LegalSection n="01" title="Acceptance of terms">
            By accessing, browsing or using this website, or by requesting a quotation, placing
            an order or otherwise transacting with Nago International, you confirm that you have
            read, understood and agree to be bound by these Terms &amp; Conditions and our{" "}
            <Link to="/privacy-policy" className="text-[#1E3A8A] font-semibold underline">
              Privacy Policy
            </Link>
            . If you do not agree, please do not use this website or our services.
          </LegalSection>

          <LegalSection n="02" title="Definitions">
            <ul>
              <li><strong>&quot;Company&quot;, &quot;we&quot;, &quot;us&quot;, &quot;our&quot;</strong> means Nago International.</li>
              <li><strong>&quot;You&quot;, &quot;your&quot;, &quot;customer&quot;</strong> means any person or entity using the website or purchasing products or services from us.</li>
              <li><strong>&quot;Products&quot;</strong> means industrial lubricants, cutting tools, precision tooling, inserts, abrasives, hardware, safety equipment, packaging materials, chemicals, custom machines and related goods we supply.</li>
              <li><strong>&quot;Services&quot;</strong> means technical support, lubrication management, preventive maintenance guidance, product consultation, sourcing and related services we offer.</li>
              <li><strong>&quot;Principal / Brand Owner&quot;</strong> means the manufacturer or brand whose products we are authorized to distribute or supply.</li>
            </ul>
          </LegalSection>

          <LegalSection n="03" title="About Nago International">
            <p>
              Nago International is a professionally managed industrial solutions and supply
              company headquartered in Belagavi, Karnataka, India. We are authorized dealers and
              distributors of leading industrial brands and supply a broad range of industrial
              products, alongside technical support and customized engineering solutions.
            </p>
            <div className="not-prose grid sm:grid-cols-2 gap-4 my-4 text-sm">
              <InfoTile k="Registered address" v={COMPANY.address} />
              <InfoTile k="GSTIN" v={COMPANY.gstin} />
              <InfoTile k="Udyam Registration" v={COMPANY.udyam} />
              <InfoTile k="Email" v={COMPANY.email} />
            </div>
          </LegalSection>

          <LegalSection n="04" title="Use of the website">
            <p>You may use this website only for lawful purposes and in accordance with these terms. You agree not to:</p>
            <ul>
              <li>Use the website in any way that breaches applicable laws or regulations;</li>
              <li>Attempt to gain unauthorized access to the website, its servers, or any connected systems;</li>
              <li>Introduce viruses, malware or other harmful material;</li>
              <li>Copy, reproduce, scrape, republish or exploit any content without our prior written permission;</li>
              <li>Use the website to transmit unsolicited or unauthorized advertising or promotional material.</li>
            </ul>
          </LegalSection>

          <LegalSection n="05" title="Products & information accuracy">
            We make reasonable efforts to ensure that product descriptions, specifications,
            dimensions, packaging sizes and images on this website are accurate. However, such
            information is provided for general reference only and is <strong>subject to change
            by manufacturers without prior notice</strong>. All product images are illustrative.
            Actual products, specifications, colours, packaging and appearance may vary. Please
            verify specific technical requirements before placing an order.
          </LegalSection>

          <LegalSection n="06" title="Brands & third-party trademarks">
            Nago International acts as an <strong>Authorized Dealer, Distributor, Supplier or
            Channel Partner</strong> for selected brands and products. All product names, brand
            names, logos, trademarks and registered trademarks displayed on this website are the
            property of their respective owners and are used solely for identification,
            informational and business-representation purposes.
          </LegalSection>

          <LegalSection n="07" title="Quotations, orders & pricing">
            <ul>
              <li>Quotations are valid for the period stated on them, and are subject to stock availability and confirmation at the time of order.</li>
              <li>Prices are subject to change without notice and, unless otherwise stated, are <strong>exclusive of GST, freight, insurance and other applicable taxes and charges</strong>.</li>
              <li>We reserve the right to accept or decline any order, in whole or in part.</li>
              <li>Minimum order quantities, packaging units and lead times may apply.</li>
            </ul>
          </LegalSection>

          <LegalSection n="08" title="Payment terms">
            <p>Payment terms for each order will be as agreed in writing on the applicable quotation, proforma invoice or tax invoice. Unless otherwise agreed:</p>
            <ul>
              <li>Payment is due as per the terms specified on the invoice;</li>
              <li>Goods may remain the property of Nago International until payment is received in full;</li>
              <li>We reserve the right to charge interest on overdue amounts and to withhold further supply where payments are outstanding.</li>
            </ul>
          </LegalSection>

          <LegalSection n="09" title="Delivery & supply">
            We supply products to customers across India. Delivery timelines communicated by us
            are estimates only and are not guaranteed. We shall not be liable for delays caused
            by manufacturers, transporters, or circumstances beyond our reasonable control.
          </LegalSection>

          <LegalSection n="10" title="Custom machines & engineering">
            Unless expressly stated otherwise, customized industrial machines and engineering
            solutions referenced on this website are <strong>not manufactured in-house</strong>{" "}
            by Nago International. Such solutions are facilitated through our network of trusted
            manufacturing partners, technology providers and authorized business associates.
          </LegalSection>

          <LegalSection n="11" title="Warranties & returns">
            Products are supplied with such warranties (if any) as are provided by the
            respective manufacturers, and subject to the manufacturers&apos; terms. Any claim relating
            to shortage, damage in transit or non-conformity must be notified to us in writing
            within 7 days of delivery.
          </LegalSection>

          <LegalSection n="12" title="Intellectual property">
            All content on this website that belongs to Nago International — including text,
            layout, graphics, design, and the Nago International name and logo — is owned by or
            licensed to us and is protected by applicable intellectual-property laws.
          </LegalSection>

          <LegalSection n="13" title="Limitation of liability">
            To the maximum extent permitted by applicable law, Nago International shall not be
            liable for any indirect, incidental, special, consequential or punitive loss or
            damage, or for any loss of profit, revenue, business, goodwill or data.
          </LegalSection>

          <LegalSection n="14" title="Indemnity">
            You agree to indemnify and hold harmless Nago International, its directors, employees
            and associates from any claims, damages, losses, liabilities and expenses arising
            out of your breach of these terms.
          </LegalSection>

          <LegalSection n="15" title="Third-party links">
            This website may contain links to third-party websites or resources. We do not
            control and are not responsible for the content, products, services or privacy
            practices of any third-party sites.
          </LegalSection>

          <LegalSection n="16" title="Force majeure">
            We shall not be liable for any failure or delay in performance caused by events
            beyond our reasonable control.
          </LegalSection>

          <LegalSection n="17" title="Governing law & jurisdiction">
            These Terms &amp; Conditions are governed by and construed in accordance with the
            laws of India. Any dispute arising out of or in connection with these terms or your
            use of this website shall be subject to the <strong>exclusive jurisdiction of the
            courts at Belagavi, Karnataka, India</strong>.
          </LegalSection>

          <LegalSection n="18" title="Changes to these terms">
            We may revise these Terms &amp; Conditions at any time without prior notice. The
            version published on this website at the time you use it applies to that use.
          </LegalSection>

          <LegalSection n="19" title="Contact us">
            <p>
              Nago International
              <br />
              {COMPANY.address}
              <br />
              Phone: {COMPANY.phones.join(" · ")}
              <br />
              WhatsApp: <a href={COMPANY.whatsappLink}>{COMPANY.whatsapp}</a>
              <br />
              Email: <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
              <br />
              Business hours: {COMPANY.hours}
            </p>
          </LegalSection>
        </article>
      </section>
    </div>
  );
}

function LegalSection({ n, title, children }) {
  return (
    <section className="mt-10">
      <h2 className="!mt-0 flex items-baseline gap-3 !text-xl !font-display !font-extrabold text-slate-900">
        <span className="text-[#E30613] font-mono text-sm">{n}</span>
        {title}
      </h2>
      <div className="mt-3 text-slate-700 leading-relaxed">{children}</div>
    </section>
  );
}

function InfoTile({ k, v }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{k}</div>
      <div className="mt-1 text-sm text-slate-800 font-medium">{v}</div>
    </div>
  );
}
